// ============================================================================
// Mystic Finance → Morpho oracle-prices adapter. OPTIONAL, key-gated.
//
// Mystic ships a Morpho Blue fork on chains the official Morpho API does not
// index (Flare 14, Plume 98866, Citrea 4114). Their `morphoCache` endpoint
// carries per-market USD prices for both sides of every market, plus the
// market id, oracle address, and token decimals — i.e. enough to feed the same
// `processMarketsToEntries` shaping the official Morpho GraphQL path uses.
//
// It is NOT the source of record for these chains, and must not become one:
//
//  - It carries no per-market oracle ratio (`state.price` in the official
//    API), so every entry it emits has `price === 0`. Only the on-chain
//    `price()` multicall produces the ratio, which is what any cross-asset
//    conversion actually needs.
//  - Since 2026-09 it requires an `x-api-key` (see `mysticApiKey.ts`) that we
//    do not hold, so absent a key this path can only ever contribute nothing.
//
// The on-chain Morpho oracle multicall covers these chains on its own — it
// reads the roster `lender-metadata` publishes and derives USD from whatever
// other lenders on the chain have already priced (Kinetic/Enosys on Flare,
// Avalon/LayerBank on Plume). So this adapter runs ONLY when a key is set, as
// a supplement that fills markets the on-chain roster does not list; with no
// key `mysticPricesAvailable` is false and the chain routes straight to the
// on-chain path instead of paying a 401 round trip per cycle.
// ============================================================================

import { processMarketsToEntries } from './morpho'
import { getMysticApiKey } from './mysticApiKey'
import { hasMysticApi } from './fetchMysticApi'
import type { OraclePriceEntry } from '../types'
import type { MorphoGqlMarket } from './morpho'

const FULL_URL = 'https://api.mysticfinance.xyz/morphoCache/'

/**
 * Should the Mystic remote run for this chain at all?
 *
 * A Mystic chain with no configured key has no usable remote source, so the
 * caller must classify it as on-chain-only rather than "remote available,
 * remote failed" — the latter suppresses nothing today but reports the chain
 * as degraded when it is in fact serving its normal, complete source.
 */
export function mysticPricesAvailable(chainId: string): boolean {
  return hasMysticApi(chainId) && Boolean(getMysticApiKey())
}

/**
 * Chains already reported as unauthorized. The endpoint requires an
 * `x-api-key` (see `mysticApiKey.ts`); without one EVERY run 401s on EVERY
 * Mystic chain, so log the condition once per chain instead of twice a minute.
 */
const warnedUnauthorized = new Set<string>()

interface MysticMarketPriceRaw {
  marketId?: string
  loanToken?: string
  collateralToken?: string
  oracle?: string
  assetPrice?: number
  collateralPrice?: number
  asset?: { asset?: string; decimals?: number; pair?: string }
  collateral?: { asset?: string; decimals?: number; pair?: string }
}

interface MysticPriceResponse {
  markets?: MysticMarketPriceRaw[]
}

export async function fetchMorphoMysticPrices(
  chainId: string,
): Promise<OraclePriceEntry[] | null> {
  try {
    const apiKey = getMysticApiKey()
    const res = await fetch(`${FULL_URL}?chainId=${chainId}`, {
      headers: apiKey ? { 'x-api-key': apiKey } : {},
    })
    if (!res.ok) {
      // 401 is a standing configuration state, not a transient failure: it
      // persists until a valid key is set, so warn once per chain per process.
      // Callers reach this only with a key configured (`mysticPricesAvailable`
      // gates the keyless case), so this means the key was REJECTED. Morpho
      // prices are not lost — the on-chain multicall still runs — but markets
      // absent from the published roster will not be filled.
      if (res.status === 401) {
        if (!warnedUnauthorized.has(chainId)) {
          warnedUnauthorized.add(chainId)
          console.warn(
            `[morpho-mystic] chain ${chainId}: 401 Unauthorized — the configured x-api-key was rejected; falling back to on-chain Morpho oracle prices only`,
          )
        }
        return null
      }
      console.warn(
        `[morpho-mystic] chain ${chainId}: ${res.status} ${res.statusText}`,
      )
      return null
    }
    const body = (await res.json()) as MysticPriceResponse
    const raw = body.markets ?? []

    const allMarkets: MorphoGqlMarket[] = []
    for (const m of raw) {
      const loanAddr = (m.loanToken ?? m.asset?.asset ?? '').toLowerCase()
      const collateralAddr = (
        m.collateralToken ??
        m.collateral?.asset ??
        ''
      ).toLowerCase()
      if (!m.marketId || !m.oracle || !loanAddr || !collateralAddr) continue

      const loanPrice =
        typeof m.assetPrice === 'number' && Number.isFinite(m.assetPrice)
          ? m.assetPrice
          : null
      const collateralPrice =
        typeof m.collateralPrice === 'number' &&
        Number.isFinite(m.collateralPrice)
          ? m.collateralPrice
          : null

      allMarkets.push({
        uniqueKey: m.marketId,
        oracleAddress: m.oracle,
        loanAsset: {
          address: loanAddr,
          decimals: m.asset?.decimals ?? 18,
          priceUsd: loanPrice,
        },
        collateralAsset: {
          address: collateralAddr,
          decimals: m.collateral?.decimals ?? 18,
          priceUsd: collateralPrice,
        },
        // Mystic does not return the on-chain oracle ratio.
        state: null,
      })
    }

    return processMarketsToEntries(chainId, allMarkets, 'morpho-mystic')
  } catch (err) {
    console.warn(`[morpho-mystic] chain ${chainId}: fetch error, skipping`, err)
    return null
  }
}
